import type {UrlParams} from 'src/types';
import {historyReplaceState} from '@stripe-internal/safe-links';

// Webpack sets NODE_ENV environment variable when developing locally
export const isDevEnvironment = () => process.env.NODE_ENV === 'development';

export const isMobile = () => {
  // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/maxTouchPoints
  // if we have more than 0 touch points and the screen size is small, we assume the user is on a mobile device
  return window.navigator.maxTouchPoints > 0 && window.innerWidth < 768;
};

let cachedUrlParams: UrlParams | null = null;

const parseUrlParams = (params: URLSearchParams): UrlParams => {
  const theme = params.get('theme');
  return {
    embedded: params.get('embedded') === 'true',
    theme: theme === 'dark' || theme === 'light' ? theme : null,
    selectedToken: params.get('selectedToken'),
    selectedWallet: params.get('selectedWallet'),
  };
};

export const getUrlParams = (): UrlParams => {
  if (cachedUrlParams) {
    return cachedUrlParams;
  }

  cachedUrlParams = parseUrlParams(new URLSearchParams(window.location.search));
  return cachedUrlParams;
};

export const setUrlParam = <Key extends keyof UrlParams>(
  key: Key,
  value: UrlParams[Key],
  wrapper?: {syncParentUrl: () => void},
) => {
  const url = new URL(window.location.href);

  if (value === null) {
    url.searchParams.delete(key);
  } else {
    url.searchParams.set(key, String(value));
  }

  historyReplaceState(window.history.state, url.toString());
  cachedUrlParams = parseUrlParams(url.searchParams);
  wrapper?.syncParentUrl();
};

let cachedIsInIframe: boolean | null = null;

export const isInIframe = (): boolean => {
  if (cachedIsInIframe !== null) {
    return cachedIsInIframe;
  }
  try {
    cachedIsInIframe = window.self !== window.top;
  } catch {
    // Cross-origin iframes throw SecurityError when accessing window.top
    cachedIsInIframe = true;
  }
  return cachedIsInIframe;
};

export const isEmbedded = (): boolean =>
  getUrlParams().embedded || isInIframe();

export const getThemeParam = () => getUrlParams().theme;
